import React, { useState, useEffect } from 'react';
import Modal, {
  ModalPrimaryButton,
  ModalSecondaryButton,
} from '../common/Modal';

const EmailPreviewModal = ({ isOpen, onClose, onConfirm, previews, isLoading, title, isMock, currentUserEmail }) => {
  const [sendToMe, setSendToMe] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setSendToMe(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm(sendToMe ? currentUserEmail : null);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title || "Email Preview"}
      subtitle={
        isMock
          ? "Safe mode - mock data"
          : "Production mode - real data"
      }
      icon={isMock ? "fa-vial" : "fa-envelope-open-text"}
      tone={isMock ? "success" : "danger"}
      size="2xl"
      closeOnBackdrop={!isLoading}
      closeOnEscape={!isLoading}
      footer={
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3 w-full">
          <div className="text-xs text-gray-400 font-medium order-2 sm:order-1">
            {previews ? `${previews.length} email(s) ready` : "0 emails"}
          </div>

          <div className="flex items-center gap-3 order-1 sm:order-2 w-full sm:w-auto flex-wrap justify-end">
            {currentUserEmail && (
              <label
                className={`flex items-center gap-2 text-sm px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                  sendToMe
                    ? "bg-sunrise-red/5 border-sunrise-red/30 text-sunrise-red"
                    : "bg-white border-pale-dawn text-grey-dusk hover:bg-pale-dawn/30"
                }`}
              >
                <input
                  type="checkbox"
                  checked={sendToMe}
                  onChange={(e) => setSendToMe(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <span>Force send to me ({currentUserEmail})</span>
              </label>
            )}

            <ModalSecondaryButton onClick={onClose} disabled={isLoading}>
              Cancel
            </ModalSecondaryButton>

            <ModalPrimaryButton
              onClick={handleConfirm}
              loading={isLoading}
              loadingText="Processing..."
              tone={isMock ? "success" : "default"}
              disabled={!previews || previews.length === 0}
            >
              <i className="fas fa-paper-plane"></i>
              {isMock ? "Send test reports" : "Send reports"}
            </ModalPrimaryButton>
          </div>
        </div>
      }
    >
        <div>
          
          {/* Context Banner */}
          <div className={`mb-6 p-4 rounded-lg border flex items-start gap-4 shadow-sm ${isMock ? 'bg-green-50 border-green-200 text-green-800' : 'bg-blue-50 border-blue-200 text-blue-800'}`}>
            <i className={`fas ${isMock ? 'fa-info-circle' : 'fa-info'} mt-1 text-lg`}></i>
            <div className="text-sm leading-relaxed">
                {isMock ? (
                    <p>
                        <strong>Test Report Simulation:</strong> You are about to generate a report using <u>mock data</u>. 
                        This is safe to send to yourself for validation. No real user data will be accessed or exposed.
                    </p>
                ) : (
                    <p>
                        <strong>Production Report:</strong> You are about to generate a report based on <u>REAL user data</u>. 
                        Please review the recipients carefully before sending.
                    </p>
                )}
            </div>
          </div>

          <div className="space-y-6">
            {previews && previews.length > 0 ? (
                previews.map((preview, index) => (
                <div key={index} className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden transition-all hover:shadow-md">
                    {/* Email Header */}
                    <div className="bg-gray-50/80 px-4 py-3 border-b border-gray-100 text-sm">
                        <div className="grid grid-cols-[80px_1fr] gap-y-2 gap-x-4 items-center">
                            <span className="font-semibold text-gray-500 text-right">To:</span>
                            <span className={`font-mono px-2 py-0.5 rounded border w-fit ${sendToMe ? 'bg-blue-50 text-blue-800 border-blue-200 font-bold' : 'bg-white text-gray-900 border-gray-200'}`}>
                                {sendToMe && currentUserEmail ? currentUserEmail : preview.recipient}
                            </span>
                            
                            <span className="font-semibold text-gray-500 text-right">Subject:</span>
                            <span className="text-gray-900 font-medium">{preview.subject}</span>
                            
                            {preview.profession && (
                                <>
                                    <span className="font-semibold text-gray-500 text-right">Type:</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-50 text-indigo-700 border border-indigo-100 w-fit">
                                        {preview.profession}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    {/* Email Body */}
                    <div className="bg-white border-t border-gray-100">
                        <iframe 
                            srcDoc={preview.body}
                            title={`Email Preview ${index}`}
                            className="w-full h-[600px] border-0"
                            sandbox="allow-same-origin"
                        />
                    </div>
                </div>
                ))
            ) : (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4 text-gray-300">
                        <i className="fas fa-inbox text-3xl"></i>
                    </div>
                    <p className="font-medium text-gray-500">No emails generated for this simulation.</p>
                    <p className="text-sm mt-1 text-gray-400">Check if there is relevant data (grades/students) to report on.</p>
                </div>
            )}
          </div>
        </div>

    </Modal>
  );
};


export default EmailPreviewModal;
